import { useMemo } from 'react';
import { SimpleGrid, Card, Text, Stack, Loader, Center } from '@mantine/core';
import { getThemeConfig } from '@/config/themes';
import classes from './ThemeSelector.module.css';
import type { Theme } from '@/lib/api-client';

interface ThemeSelectorProps {
  onSelectTheme: (themeSlug: string) => void;
  selectedTheme?: string;
  themes?: Theme[];
  isLoading?: boolean;
}

export function ThemeSelector({ onSelectTheme, selectedTheme, themes, isLoading }: ThemeSelectorProps) {

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader color="teal" />
      </Center>
    );
  }

  if (!themes || themes.length === 0) {
    return (
      <Text c="dimmed" ta="center">
        No themes available
      </Text>
    );
  }

  // Sort selected theme to the top
  const sortedThemes = useMemo(() => {
    if (!selectedTheme || !themes) return themes || [];
    return [...themes].sort((a, b) => {
      if (a.id === selectedTheme) return -1;
      if (b.id === selectedTheme) return 1;
      return 0;
    });
  }, [themes, selectedTheme]);

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
      {sortedThemes.map((theme) => {
        const themeConfig = getThemeConfig(theme.slug);
        const isSelected = selectedTheme === theme.id;

        return (
          <Card
            key={theme.id}
            shadow="sm"
            padding="lg"
            withBorder
            className={`${classes.card} ${isSelected ? classes.selected : ''}`}
            onClick={() => onSelectTheme(theme.id)}
            data-theme-color={themeConfig.color}
          >
            <Stack gap="xs">
              <Text size="52px" ta="center" fw={800} lh={1}>
                {theme.emoji || themeConfig.emoji || '📚'}
              </Text>
              <Text fw={600} ta="center">
                {theme.label}
              </Text>
              <Text size="sm" c="dimmed" ta="center" lineClamp={2}>
                {theme.subtitle || theme.description || theme.prompt_text}
              </Text>
            </Stack>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}
